import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {isUserPremium} from '@Libraries/user.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateIsSpellCheckEnabledForUser} from '@Libraries/spell-check-library';
import {getIsSpellCheckEnabled, updateIsSpellCheckEnabled, updateIsSpellCheckPreferenceUpdating} from '@/reducers/user-preferences.reducer';

export function TextSpellcheckOption(): ReactElement {
  const isSpellCheckEnabled = useAppSelector(getIsSpellCheckEnabled);
  const isSpellCheckPreferenceUpdating = useAppSelector((state) => {
    return state.userPreferences.isSpellCheckPreferenceUpdating;
  });
  const currentUserId = useAppSelector((state) => {
    return state.posterEditor.currentUserId;
  });
  const dispatch = useAppDispatch();

  const updateSpellCheckToggleOnUserLogin = async (): Promise<void> => {
    dispatch(updateIsSpellCheckPreferenceUpdating(true));
    try {
      const response = (await window.PMW.writeLocal('spellcheck/isSpellCheckEnabled')) as boolean;
      dispatch(updateIsSpellCheckEnabled(response));
      await window.posterEditor?.whiteboard?.getCurrentPage().spellCheck.checkSpellForSelectedObject();
    } finally {
      dispatch(updateIsSpellCheckPreferenceUpdating(false));
    }
  };

  useEffect(() => {
    if (currentUserId && !isSpellCheckEnabled) {
      void updateSpellCheckToggleOnUserLogin();
    }
  }, [currentUserId]);

  const onClickHandler = (): void => {
    if (isUserPremium()) {
      void updateIsSpellCheckEnabledForUser(!isSpellCheckEnabled);
    } else {
      window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK);
    }
  };

  const getTag = (): ReactElement | null => {
    if (isUserPremium()) {
      return null;
    }
    return <Icon className="color-premium" icon="icon-crown" size={IconSize.SIZE_ICON_20} />;
  };

  return (
    <ContextualMenuToggleOption
      id="text-spellcheck-option"
      label={window.i18next.t('pmwjs_spellcheck')}
      tag={getTag()}
      isLoading={isSpellCheckPreferenceUpdating}
      isChecked={isSpellCheckEnabled}
      onClick={onClickHandler}
    />
  );
}
